// THE ROUTE LADDER — an ordered list of fallback rungs a purpose route walks once its
// own pin cannot serve (owner, 2026-11-01: "sol, then glm 5.3, then glm flash, then
// openrouter — in this order the priority").
//
// Before it, a bound route named ONE key (`route-bound.ts`, `@developerz/agent`), and
// what ran when that key's pinned model cooled was decided by the job class's TIER
// chain (`JOB_CLASS_ROUTES`) over the key's catalogue tags. Measured, tesote
// 2026-11-01: the `scout` / `debugging-qa` lanes (`[smart, general]`) fell from a
// cooling `gpt-6.1-sol` to `gpt-6-luna` (13 sessions that day, PR #3305 built on it),
// the `general` / `review` lanes (`[general, fast]`) skipped `glm-5.3` for
// `glm-5.3-flash`, and no lane could reach a key the row did not name. The tier chain
// is a per-JOB-CLASS default; an account's model priority is a per-ROUTE fact, so it
// is stored on the route.
//
// A rung names a `provider` and/or a `model` — never a key label. A label is renamed
// in place (`renameOrgKeyLabel` moves `llm_routes.key_label` under its own lock); a
// label inside a JSON list would be stranded by it. Provider + model is stable across
// renames and is what an operator means by "then OpenRouter".

import { z } from 'zod';
import { BYOKProviderSchema } from '../entities/byok';

/**
 * One fallback rung. At least one axis: a rung naming nothing would match every key,
 * which is "the whole pool", the very widening a bound route exists to refuse.
 * - `provider` + `model`: that model, on any key of that provider that serves it live;
 * - `provider` alone: any key of that provider, on the model the pool resolver pairs it with;
 * - `model` alone: that model, on any key that serves it live.
 */
export const LlmRouteRungSchema = z
  .strictObject({
    provider: BYOKProviderSchema.optional(),
    model: z.string().trim().min(1).max(200).optional(),
  })
  .refine((r) => r.provider !== undefined || r.model !== undefined, {
    message: 'a fallback rung names a provider, a model, or both',
  });
export type LlmRouteRung = z.infer<typeof LlmRouteRungSchema>;

/**
 * At most this many rungs. Not a budget: each rung is one walk over the account's pool
 * on every claim, and no real priority list is longer than the number of distinct
 * models an account holds keys for. A longer list is a mistake (a pasted catalogue),
 * refused at the write edge rather than walked forever.
 */
export const LLM_ROUTE_MAX_FALLBACKS = 8;

/** The stored list: ordered, first tried first, empty = no ladder (the route alone). */
export const LlmRouteFallbacksSchema = z.array(LlmRouteRungSchema).max(LLM_ROUTE_MAX_FALLBACKS);
export type LlmRouteFallbacks = readonly LlmRouteRung[];
